import type { Question } from "@shared/schema";

// The ARC-39 duplicate advisory, deferred to ARC-43 until a stable target
// identity existed. A duplicate marker carries the target's BCE session-level
// question_index, which is meaningless once the teacher reorders or edits the
// test, so the rendered message is chosen only after that index has been
// resolved to a current display number. Unresolvable markers fall back to the
// numberless wording: guessing a number would point the teacher at the wrong
// item again, which is the defect this whole ticket exists to stop.

/** Advisory shown when the duplicate's target index resolves to a live item. */
export const DUPLICATE_ADVISORY_RESOLVED =
  "This item may be too similar to Item {n}. Verify both are testing different content. If they are too similar, regenerate one of the items.";

/** Advisory shown when the target index cannot be resolved to a display item. */
export const DUPLICATE_ADVISORY_GENERIC =
  "This item may be similar to another item in this test. Verify items are testing different content.";

export function duplicateAdvisoryText(targetDisplayNumber: number | null): string {
  if (targetDisplayNumber == null) return DUPLICATE_ADVISORY_GENERIC;
  return DUPLICATE_ADVISORY_RESOLVED.replace("{n}", String(targetDisplayNumber));
}

/**
 * Resolve a duplicate marker's BCE-side 0-based `duplicate_of_question_index`
 * to the number the teacher currently sees on this item's card. The index is
 * a session-level position in the BCE feedback, which does not survive
 * reordering or edits, so it is dereferenced through the stable identity
 * chain: index -> task_id (from the session feedback map), then task_id ->
 * display position (from the current questions array).
 *
 * Returns null, and callers must fall back to the numberless advisory, when:
 *   - the marker carries no index (duplicate_of_question_index is null)
 *   - no feedback row maps that index to a task_id (task_id absent on target)
 *   - the task_id no longer matches any question (item removed from the test)
 */
export function resolveTargetDisplayNumber(
  duplicateOfQuestionIndex: number | null | undefined,
  questionIndexToTaskId: ReadonlyMap<number, string> | undefined,
  questions: Question[],
): number | null {
  if (duplicateOfQuestionIndex == null) return null;
  const taskId = questionIndexToTaskId?.get(duplicateOfQuestionIndex);
  if (!taskId) return null;
  const index = questions.findIndex((q) => q.taskId === taskId);
  return index >= 0 ? index + 1 : null;
}

/**
 * Build the session-level BCE question_index -> task_id map from a Socrates
 * score response. This is the non-positional identity layer that lets a
 * duplicate marker be resolved later, after the teacher has reordered the
 * test or removed items.
 */
export function buildQuestionIndexToTaskId(
  socratesQuestions: ReadonlyArray<{ question_index: number; task_id?: string }>,
): Map<number, string> {
  const map = new Map<number, string>();
  for (const sq of socratesQuestions) {
    if (sq.task_id) map.set(sq.question_index, sq.task_id);
  }
  return map;
}